import { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { TabViewTableSkeleton } from '@/components/TabView';
import { CanUploadFilesContext, UploadFilesContext } from '@trinity/chat-components/containers/WorkspaceFileUpload/Context.js';
import { useInitiateFilesReview } from '@trinity/chat-components/containers/WorkspaceFileUpload/hooks/useInitiateFilesReview.js';
import { FileUploadReviewContainer } from '@/containers/WorkspaceFileUpload/Upload.Review';
import { ProjectDetailsContext, ProjectStateLoadingContext } from '@/screens/Project.Main/Context';
import { FilesTable, FilesTableDataContext, FilesTableLoadingContext, FilesTableContext, SelectedObjectsContainer } from '@/screens/Workspace.FilesTable';
import styles from './style/Project.Files.module.css';

type Props = unknown;

const ProjectFilesTable = (props: Props) => {

  const loading = useContext(FilesTableLoadingContext);

  return (
    <div>
      {loading
        ? <TabViewTableSkeleton />
        : (
          <div className={styles.root}>
            <div className={styles.wrap}>
              <FilesTable
                classes={{
                  root: styles.content,
                  table: styles.table,
                  header: styles.header,
                }} />
            </div>
          </div>
        )
      }
    </div>
  );
};

type Params = {
  projectId: string;
};

const ProjectFiles = (props: Props) => {
  const ctx = {
    details: useContext(ProjectDetailsContext),
    loading: useContext(ProjectStateLoadingContext),
  };
  const params = useParams<Params>();

  const apiClient = useChatApiClient();

  const query = useQuery({
    queryKey: [
      `get:projects/files`,
      +params.projectId,
      ctx.details.query.data?.project?.parentId,
    ],
    queryFn: () => {
      return apiClient.projects.fetchProjectFiles({
        projectId: +params.projectId,
      });
    },
    enabled: !ctx.loading,
  });

  const uploadFiles = useInitiateFilesReview({
    parentObjectId: ctx.details.query.data?.object?.id,
    workspaceId: ctx.details.query.data?.object?.workspaceId,
  });

  return (
    <SelectedObjectsContainer>
      <FilesTableDataContext.Provider value={(query.data?.items) || []}>
        <FilesTableLoadingContext.Provider value={query.isLoading}>
          <FilesTableContext.Provider value={{
            parentObject: {
              id: ctx.details.query.data?.object?.id,
              workspaceId: ctx.details.query.data?.object?.workspaceId,
            },
          }}>
            <UploadFilesContext.Provider value={uploadFiles}>
              <CanUploadFilesContext.Provider value={ctx.details.query.data?.capabilities?.canAddChildren}>
                <ProjectFilesTable />
              </CanUploadFilesContext.Provider>
            </UploadFilesContext.Provider>
          </FilesTableContext.Provider>
        </FilesTableLoadingContext.Provider>
      </FilesTableDataContext.Provider>
    </SelectedObjectsContainer>
  );
};

const ProjectFilesContainer = (_: unknown) => {
  return (
    <FileUploadReviewContainer>
      <ProjectFiles />
    </FileUploadReviewContainer>
  );
};

export { ProjectFilesContainer as ProjectFiles };